import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "@evertrue/et-components";
import styled from "styled-components";
import PropTypes from "prop-types";

const STEPS = {
  WARNING: 1,
  CONFIRM: 2,
  TYPE_TO_OFFBOARD: 3,
};

const REQUIRED_TEXT = "OFFBOARD";

const VoipOffboardModal = ({ isOpen, onClose, oid, onConfirm }) => {
  const [step, setStep] = useState(STEPS.WARNING);
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setStep(STEPS.WARNING);
    setInputValue("");
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      handleClose();
    } catch (_error) {
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConfirmDisabled =
    inputValue.trim().toUpperCase() !== REQUIRED_TEXT || isSubmitting;

  return (
    <Modal isOpen={isOpen} closeModal={handleClose}>
      {step === STEPS.WARNING && (
        <React.Fragment>
          <ModalHeader title="Offboard VOIP" closeModal={handleClose} />
          <ModalBody>
            <WarningTitle>Warning: This action is irreversible</WarningTitle>
            <WarningText>
              Offboarding will permanently remove the Twilio VOIP configuration
              for this organization. All VOIP settings, including the TwiML app
              and associated phone numbers, will be disconnected.
            </WarningText>
            <WarningText>
              Once offboarded, the organization will lose all VOIP capabilities
              and will need to be re-onboarded to restore them.
            </WarningText>
          </ModalBody>
          <FooterActions>
            <Button type="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="destroy" onClick={() => setStep(STEPS.CONFIRM)}>
              Continue
            </Button>
          </FooterActions>
        </React.Fragment>
      )}

      {step === STEPS.CONFIRM && (
        <React.Fragment>
          <ModalHeader title="Are You Sure?" closeModal={handleClose} />
          <ModalBody>
            <WarningTitle>
              Are you sure you want to offboard org ID{" "}
              <OrgIdHighlight>{oid}</OrgIdHighlight>?
            </WarningTitle>
            <WarningText>
              This will remove the VOIP configuration for this organization.
              This cannot be undone.
            </WarningText>
          </ModalBody>
          <FooterActions>
            <Button type="secondary" onClick={() => setStep(STEPS.WARNING)}>
              Go Back
            </Button>
            <Button
              type="destroy"
              onClick={() => setStep(STEPS.TYPE_TO_OFFBOARD)}
            >
              Yes, Continue
            </Button>
          </FooterActions>
        </React.Fragment>
      )}

      {step === STEPS.TYPE_TO_OFFBOARD && (
        <React.Fragment>
          <ModalHeader title="Final Confirmation" closeModal={handleClose} />
          <ModalBody>
            <WarningTitle>Type to confirm offboarding</WarningTitle>
            <WarningText>
              To confirm, please type{" "}
              <ConfirmKeyword>{REQUIRED_TEXT}</ConfirmKeyword> in the box below
              and click Submit.
            </WarningText>
            <label htmlFor="voip-offboard-confirm">
              Type <strong>{REQUIRED_TEXT}</strong> to confirm
            </label>
            <ConfirmInput
              id="voip-offboard-confirm"
              type="text"
              placeholder={REQUIRED_TEXT}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              autoComplete="off"
            />
          </ModalBody>
          <FooterActions>
            <Button
              type="secondary"
              onClick={() => {
                setInputValue("");
                setStep(STEPS.CONFIRM);
              }}
            >
              Cancel
            </Button>
            <Button
              type="destroy"
              disabled={isConfirmDisabled}
              onClick={handleSubmit}
            >
              {isSubmitting ? "Offboarding..." : "Submit"}
            </Button>
          </FooterActions>
        </React.Fragment>
      )}
    </Modal>
  );
};

const WarningTitle = styled.p`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
`;

const WarningText = styled.p`
  margin-bottom: 12px;
  line-height: 1.5;
`;

const OrgIdHighlight = styled.span`
  font-weight: 700;
  font-family: monospace;
`;

const ConfirmKeyword = styled.strong`
  font-family: monospace;
  letter-spacing: 0.05em;
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px 16px;
`;

VoipOffboardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  oid: PropTypes.number,
  onConfirm: PropTypes.func.isRequired,
};

VoipOffboardModal.defaultProps = {
  oid: 0,
};

export default VoipOffboardModal;
